"use client";

import React, { useEffect, useState } from "react";
import { IPlatform, IPlugin } from "../../types";
import { StyledDiv } from "./installGuide.styles";
import Image from "next/image";
import { CustomLink } from "../link/link.comp";
import { IoIosArrowForward } from "@react-icons/all-files/io/IoIosArrowForward";
import { CgZoomIn } from "@react-icons/all-files/cg/CgZoomIn";
import { Popup } from "../popup/popup.comp";

interface IInstallGuideProps {
  instructions?: any;
  platform?: IPlatform;
  app: IPlugin;
  variant?: "slider" | "list";
  text?: string;
}

export const InstallGuide = ({
  instructions,
  platform,
  app,
  variant = "slider",
  text,
}: IInstallGuideProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(-1);

  const [steps, setSteps] = useState([
    {
      title: platform
        ? "Create a [app_name] [app_type] for [platform] on Common Ninja"
        : "Create a [app_name] [app_type] for your website on Common Ninja",
      step: platform
        ? `First, create a [platform] [app_name] [app_type] on Common Ninja.
On the [app_type]s catalog, find the [app_name] and enter the Editor.`
        : `First, create a [app_name] [app_type] on Common Ninja.
On the [app_type]s catalog, find the [app_name] and enter the Editor.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1689530722416_common-ninja-editor.png",
    },
    {
      title: "Save the [app_name] [app_type]",
      step: `When you're done creating the [app_name] [app_type], click on the Save Changes button at the right top corner of the editor.
If you don't have an account on Common Ninja, you'll have to register first.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1689531419429_how-to-add-widget-save-changes.jpg",
    },
    {
      title: "Copy the [app_name] Embed Code",
      step: platform
        ? `When you're ready to add the [app_name] [app_type] to your [platform] [site_type], click the Add To Website button at the top bar of the Editor.
On the window that opens, click the Copy Icon to copy the [app_type] embed code. In the next steps, you will paste this code into your [platform] [site_type].`
        : `When you're ready to add the [app_name] [app_type] to your website, click the Add To Website button at the top bar of the Editor.
On the window that opens, click the Copy Icon to copy the [app_type] embed code. In the next steps, you will paste this code into your website.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1689531885051_how-to-add-widget-embed-code.jpg",
    },
    {
      title: "Paste the code to your [platform] website’s HTML field.",
      step: `Next, go to your [platform] [site_type] editor and find the HTML field. It is most likely in the form of an HTML/Code widget or module which you can find on the [platform]’s editor. Add it to your page, and paste the [app_name] Embed Code you’ve copied before to the HTML field.
Save and update, and that’s it, you should have Common Ninja’s [app_name] [app_type] on your [platform] [site_type].`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237151468_image2.png",
    },
    {
      title:
        "Alternative Method 1: Through the HTML option in the text’s editing area.",
      step: `If you can't find the HTML/Code widget or module, there's another way to put Common Ninja’s [app_name] [app_type] on your [platform] [app_type]. You can just add the code to a special HTML section in the text editor.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237160792_image3.png",
    },
    {
      title:
        "Alternative Method 2: Add it directly to your [platform] [site_type]’s HTML code.",
      step: `If you're having trouble finding a widget or a special part in your text editor where you can add the Embed Code, you can try a different way to add the [app_name] [app_type] to your [platform] [site_type]. You can do this by putting it straight into your [platform] [site_type]'s HTML code.`,
      imgUrl:
        "https://website-assets.commoninja.com/distribution/1644237172811_image4.png",
    },
  ]);

  function capitalize(s: string): string {
    if (!s) return "";
    return s[0].toUpperCase() + s.slice(1);
  }

  function replaceWords(s: string) {
    return (s || "")
      ?.replace(/\[app_type\]/g, platform?.appSynonym || "widget")
      ?.replace(/\[App_type\]/g, capitalize(platform?.appSynonym || "Widget"))
      ?.replace(/\[platform\]/g, platform?.title || "Platform")
      ?.replace(/\[site_type\]/g, platform?.siteSynonym || "website")
      ?.replace(
        /\[Site_type\]/g,
        capitalize(platform?.siteSynonym || "Website")
      )
      ?.replace(/\[app_name\]/g, app.name);
  }

  useEffect(() => {
    if (instructions && instructions.length >= 3) {
      setSteps(instructions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function renderSteps() {
    if (variant === "list") {
      return (
        <div className="list">
          <h2>
            {!text ? (
              <>
                How To Add the <span>{app.name}</span> Widget+ To Your Website
              </>
            ) : (
              text
            )}
          </h2>
          {steps.map((step, idx) => (
            <div key={idx} className="card">
              <div className="slider-text">
                <div className="title-wrapper">
                  <div className="step-dot">
                    {idx + 1 >= 10 ? `${idx + 1}` : `0${idx + 1}`}
                  </div>
                  <h5>{replaceWords(step.title) || `Step ${idx + 1}`}</h5>
                </div>
                <div
                  className="install-p"
                  dangerouslySetInnerHTML={{
                    __html: replaceWords(step.step),
                  }}
                />
              </div>
              <div
                onClick={() => setZoomedImage(idx)}
                className="img-container"
              >
                <CgZoomIn className="zoom" />
                <Image
                  src={step.imgUrl}
                  alt={replaceWords(step.title) || "install steps"}
                  style={{ cursor: "pointer" }}
                  fill
                />
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <>
        <h2>How to Install</h2>
        <div className="card">
          <div className="text-and-dots">
            <div className="step-dots">
              {steps &&
                steps.map((_, idx) => (
                  <div
                    onClick={() => setActiveStep(idx)}
                    key={`dot-step-${idx + 1}`}
                    className={`step-dot ${activeStep === idx ? "active" : ""}`}
                  >
                    {idx + 1 >= 10 ? `${idx + 1}` : `0${idx + 1}`}
                  </div>
                ))}
            </div>
            <div className="slider-text">
              <h5>
                {replaceWords(steps[activeStep].title) ||
                  `Step ${activeStep + 1}`}
              </h5>
              <div
                className="install-p"
                dangerouslySetInnerHTML={{
                  __html: replaceWords(steps[activeStep].step),
                }}
              />
            </div>
          </div>
          {steps[activeStep].imgUrl && (
            <div className="img-container">
              <Image
                src={steps[activeStep].imgUrl}
                alt={replaceWords(steps[activeStep].title)}
                fill
              />
            </div>
          )}
        </div>
        <CustomLink
          className="link"
          variant="secondary"
          href={`/widgets/${app.seoSlug || app.slug}${
            platform?.slug ? `/${platform.slug}` : ``
          }/how-to-add`}
        >
          For a Full Guide
          <IoIosArrowForward />
        </CustomLink>
      </>
    );
  }

  return (
    <>
      <StyledDiv>
        {renderSteps()}
        {app.longDescription && (
          <>
            <h2 style={{ margin: "100px 0 20px" }}>Why Use This Widget</h2>
            <p
              dangerouslySetInnerHTML={{
                __html: app.longDescription || "veru ",
              }}
            />
          </>
        )}
        <p className="credit-text">
          This tutorial was written by the Common Ninja product team, creators
          of interactive, no-code widgets for {platform?.title || ""}{" "}
          {platform?.siteSynonym || "websites"}. Common Ninja helps over 500,000
          users build engaging online experiences without coding.
        </p>
      </StyledDiv>
      <Popup
        show={zoomedImage !== -1}
        onClose={() => setZoomedImage(-1)}
        style={{
          height: "600px",
          maxWidth: "95%",
          width: "1100px",
          backgroundColor: "black",
        }}
      >
        <div className="zoomed-image">
          <Image
            className=""
            src={steps[zoomedImage]?.imgUrl}
            alt={replaceWords(steps[zoomedImage]?.title) || "install steps"}
            fill
            style={{ objectFit: "contain", borderRadius: "10px" }}
          />
        </div>
      </Popup>
    </>
  );
};
